import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bot, Grid2x2, History, MessageSquare, Settings, Sparkles, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePermission } from '@/hooks/usePermission';
import { AiChatTab } from '@/features/ai/components/AiChatTab';
import { AiActionsTab } from '@/features/ai/components/AiActionsTab';
import { AiHistoryTab } from '@/features/ai/components/AiHistoryTab';

type Tab = 'chat' | 'actions' | 'history';

/**
 * The global AI Assistant — a floating toggle + slide-over panel
 * mounted once in AppLayout, so it's present on every authenticated
 * page rather than a route-specific widget. A fixed command-style
 * helper (see command_registry.php) and rule-based search, not a
 * language model. Gated on ai.use; a full activity log lives at a
 * separate page (linked from the panel header, gated on ai.manage).
 */
export function AiAssistantPanel() {
  const canUse = usePermission('ai.use');
  const canManage = usePermission('ai.manage');
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('chat');

  if (!canUse) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label="Open AI Assistant"
        className="fixed bottom-5 right-5 z-40 flex h-[52px] w-[52px] items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition-transform hover:scale-105 hover:bg-brand-700"
      >
        <Bot className="h-5 w-5" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-40 bg-black/20"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="fixed bottom-0 right-0 top-0 z-40 flex w-full max-w-sm flex-col border-l border-gray-200 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
            >
              <div className="flex items-center gap-2 border-b border-gray-100 p-4 dark:border-gray-800">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400">
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">AI Assistant</h2>
                </div>
                {canManage && (
                  <Link to="/organization/ai-settings" onClick={() => setIsOpen(false)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                    <Settings className="h-4 w-4" />
                  </Link>
                )}
                <button type="button" onClick={() => setIsOpen(false)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="flex border-b border-gray-100 dark:border-gray-800">
                <TabButton icon={MessageSquare} label="Chat" active={tab === 'chat'} onClick={() => setTab('chat')} />
                <TabButton icon={Grid2x2} label="Actions" active={tab === 'actions'} onClick={() => setTab('actions')} />
                <TabButton icon={History} label="History" active={tab === 'history'} onClick={() => setTab('history')} />
              </div>

              <div className="flex flex-1 flex-col overflow-hidden p-4">
                {tab === 'chat' && <AiChatTab />}
                {tab === 'actions' && <AiActionsTab onNavigate={() => setIsOpen(false)} />}
                {tab === 'history' && <AiHistoryTab />}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

function TabButton({ icon: Icon, label, active, onClick }: { icon: React.ComponentType<{ className?: string }>; label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-medium ${active ? 'border-b-2 border-brand-600 text-brand-700 dark:text-brand-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}
